import type { Practitioner } from '@medplum/fhirtypes'
import { invariant } from '@seen/core/invariant'
import { CompositionNote } from '@seen/core/models/compositionNote'
import type { NoteLabel } from '@seen/core/models/noteLabel'
import { parseReferenceString } from '@seen/core/utils'
import { Button, cn, IconCalendar } from '@seen/ui'
import { IconHistory, IconLock, IconLockOpen } from '@tabler/icons-react'
import type { DefaultError } from '@tanstack/react-query'
import { type JSX, useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import {
  DatePicker,
  DropdownMenuDeleteItem,
  DropdownMenuItem,
  DropdownMenuMedplumLinkItem,
  DropdownMenuSeparator,
  HoverDate,
  Loader,
  MoreDropdownMenu,
  ResourceFieldForm,
  RichTextEditor,
  ScrollArea,
  VersionHistoryDialog,
} from '#src/components/index.ts'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { NoteLabelComboboxButton } from '#src/components/NoteLabelComboboxButton.tsx'
import { OwnerRequiredComboboxButton } from '#src/components/OwnerRequiredComboboxButton.tsx'
import { CompositionContext } from '#src/contexts/CompositionContext.tsx'
import { usePatchMutation, useRead } from '#src/hooks/index.ts'
import {
  extractFirstLineFromContent,
  parseNoteContent,
} from '#src/utils/noteContent.ts'

export function NotePage(): JSX.Element {
  const { compositionId } = useParams()
  invariant(compositionId, 'Composition ID is required')
  return <NotePageComponent compositionId={compositionId} />
}

function NotePageComponent({
  compositionId,
}: {
  compositionId: string
}): JSX.Element {
  const { data: note, isLoading } = useRead(CompositionNote, compositionId)

  const [unlocked, setUnlocked] = useState(false)
  const [isVersionHistoryDialogOpen, setIsVersionHistoryDialogOpen] =
    useState(false)

  const { mutate: patchNote, mutateAsync } = usePatchMutation<CompositionNote>()
  const navigate = useNavigate()

  const openVersionHistoryDialog = useCallback(() => {
    setIsVersionHistoryDialogOpen(true)
  }, [])

  const handleRestoreVersion = useCallback(
    (version: CompositionNote) => {
      if (!note) {
        return
      }

      patchNote({
        instance: note,
        mutateFn: (n) => {
          n.content = version.content
        },
        patchOptions: {
          skipVersionCheck: true,
        },
      })
      toast.success('Version restored')
    },
    [note, patchNote]
  )

  if (isLoading) {
    return <Loader />
  }

  if (!note) {
    return <h2>Note not found</h2>
  }

  const title = extractFirstLineFromContent(note.content) || 'Note'
  const editable = unlocked

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b px-pagex-default py-2.5 lg:px-pagex-lg">
        {editable && (
          <div className={cn('flex flex-wrap items-center gap-2')}>
            <ResourceFieldForm
              instance={note}
              valueFn={(n) => n.created}
              patchFn={(n, value) => {
                n.created = value
              }}
              render={({ field: { onChange, ...rest } }) => (
                <DatePicker
                  onChange={(d) => {
                    invariant(d, 'Date is required')
                    onChange(d)
                  }}
                  {...rest}
                  className="h-10"
                  clearText={null}
                />
              )}
            />
            <ResourceFieldForm
              instance={note}
              valueFn={(n) => n.owner.reference ?? null}
              patchFn={(n, owner) => {
                const ownerReference = parseReferenceString<Practitioner>(owner)
                invariant(ownerReference, 'Owner is required')
                n.owner = ownerReference
              }}
              render={({ field }) => <OwnerRequiredComboboxButton {...field} />}
            />
            <ResourceFieldForm
              instance={note}
              valueFn={(n) => n.labels as string[]}
              patchFn={(n, value) => {
                n.labels = value as NoteLabel['referenceString'][]
              }}
              render={({ field }) => <NoteLabelComboboxButton {...field} />}
            />
          </div>
        )}
        {!editable && (
          <div className="flex flex-wrap items-center gap-2 py-2.5">
            <div className="flex items-center gap-2 border-x border-transparent px-4 text-sm">
              <IconCalendar />
              <HoverDate date={note.created} />
            </div>
            <div className="flex items-center border-x border-transparent px-4 text-sm">
              <PractitionerNameplate
                practitioner={note.owner.reference}
                size={5}
              />
            </div>
            <ResourceFieldForm
              instance={note}
              valueFn={(n) => n.labels as string[]}
              patchFn={(n, value) => {
                n.labels = value as NoteLabel['referenceString'][]
              }}
              render={({ field }) => <NoteLabelComboboxButton {...field} />}
            />
          </div>
        )}
        <MoreDropdownMenu>
          <DropdownMenuItem onSelect={openVersionHistoryDialog}>
            <IconHistory className="size-4" />
            Version history
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuDeleteItem
            deleteConfirmationProps={{
              title: `Delete "${title}"?`,
              description:
                'Deleted notes can still be recovered by contacting our team.',
              onConfirm: () => {
                void navigate(-1)
                mutateAsync({
                  instance: note,
                  mutateFn: (t) => {
                    t.status = 'entered-in-error'
                  },
                })
                  .then(() => toast.success(`"${title}" deleted`))
                  .catch((error: DefaultError) => {
                    toast.error(`Failed to delete "${title}": ${error}`)
                  })
              },
            }}
          />
          <DropdownMenuMedplumLinkItem to={note} />
        </MoreDropdownMenu>
      </div>
      <VersionHistoryDialog
        open={isVersionHistoryDialogOpen}
        onOpenChange={setIsVersionHistoryDialogOpen}
        resource={note}
        onRestoreVersion={handleRestoreVersion}
      />
      <ScrollArea
        className="flex-1"
        scrollRestorationId={`NotePage-${compositionId}`}
      >
        <ResourceFieldForm
          instance={note}
          valueFn={(n) => n.content}
          patchFn={(n, value) => {
            n.content = value
          }}
          skipVersionCheck
          debounceTyping
          render={({ field }) => {
            // The editor receives the RAW string: RichTextEditor parses JSON-doc
            // strings itself (memoized), so content identity stays stable across
            // renders. Parsing here would hand it a fresh object every render of
            // this render prop, re-arming useSyncEditorContent's effect per
            // render. RichTextEditor outputs HTML via onChange, so JSON content
            // is naturally migrated to HTML on first edit.
            const parsedForEmptyCheck = parseNoteContent(field.value)
            const isEmpty =
              typeof parsedForEmptyCheck === 'string'
                ? !parsedForEmptyCheck.trim()
                : false
            return (
              <CompositionContext.Provider value={note.reference.reference}>
                <RichTextEditor
                  {...field}
                  value={field.value}
                  variant="unstyled"
                  contentClassName="px-pagex-default lg:px-pagex-lg py-7 max-w-(--container-prose)"
                  placeholder="Write, press '@' for commands…"
                  enableColumns
                  autofocus={isEmpty}
                  editable={editable}
                />
              </CompositionContext.Provider>
            )
          }}
        />
      </ScrollArea>
      <Button
        variant="outline"
        onClick={() => setUnlocked((locked) => !locked)}
        className="absolute right-4 bottom-4 z-10 bg-background"
        size="icon"
      >
        {unlocked ? (
          <IconLockOpen className="size-4" />
        ) : (
          <IconLock className="size-4" />
        )}
      </Button>
    </div>
  )
}
